import {
  BuildOutlined,
  CheckOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Segmented, Space, Switch } from 'antd';
import type { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import { ActiveStepSpinner } from 'components/Custom/ActiveStepSpinner';
import { turnModalOff } from 'store/app/actions';
import { parseRuleDone, parseRuleRequest } from 'store/receipt-rules/actions';
import { getParsedRule } from 'store/receipt-rules/selectors';
import {
  AuthoringChoiceCard,
  AuthoringChoiceDescription,
  AuthoringChoiceIcon,
  AuthoringChoiceRow,
  AuthoringChoiceText,
  AuthoringChoiceTitle,
  AuthoringChoiceOr,
  AuthoringAiBadge,
  ConditionBlock,
  ConditionBlockHeader,
  ConditionConnector,
  ConditionLogicRow,
  DescribeExampleChip,
  DescribeExampleList,
  DescribeExamplesHeading,
  DescribeExamplesSection,
  DescribeInputBlock,
  DescribeInputHint,
  DescribeInputLabel,
  DescribeParseFeedback,
  DescribeParseFeedbackDetail,
  DescribeParseFeedbackHeader,
  DescribeParseFeedbackIcon,
  DescribeParseFeedbackText,
  DescribeParseFeedbackTitle,
  DescribeParseSuggestionChip,
  DescribeParseSuggestionList,
  DescribeParseSuggestionsHeading,
  ExtractionRuleModalAntTypographyStyle,
  ExtractionRulesFormControlStyle,
  ExtractionRulesSelectDropdownStyle,
  FieldOptionDescription,
  FieldOptionTitle,
  FieldSelectOptionContent,
  FooterButton,
  FooterButtons,
  FooterLeft,
  FooterPrimaryButton,
  FooterRight,
  FooterRightButtons,
  MAP_SELECT_PROPS,
  filterMapSelectOption,
  ModalContent,
  ModalFooterBar,
  ModalRoot,
  ModalScrollBody,
  PreviewBox,
  ReviewEnabledHint,
  ReviewEnabledLabel,
  ReviewEnabledRow,
  ReviewEnabledTitle,
  RuleBuilderFlow,
  RuleFieldItem,
  RuleFieldLabel,
  RuleFieldList,
  RuleMapAutoComplete,
  RuleMapInput,
  RuleMapSelect,
  RuleNameInput,
  RuleNameInputWrap,
  RuleNameError,
  RuleStepBody,
  RuleStepCard,
  RuleStepChevron,
  RuleStepDescription,
  RuleStepHeader,
  RuleStepHeaderActions,
  RuleStepHeaderText,
  RuleStepNumber,
  RuleStepTitle,
  RuleStepTitleRow,
  OrangeSpinnerRing,
  Section,
  StepActionButton,
  StepBadge,
  StepEmptyHint,
} from './extraction-rules-styles';
import { DescribeFieldMentions } from './describe-field-mentions';
import { findFuzzyMatch } from './fuzzy-mapper';
import {
  getFieldValueOptions,
  getInterpretationRuleFieldSelectOptions,
  getOperatorOptionsForField,
  isOperatorValidForField,
  operatorAllowsFreeTextValue,
  operatorNeedsValue,
  type InterpretationRuleFieldSelectOption,
} from './interpretation-rule-field-options';
import {
  cloneActionsForForm,
  cloneConditionsForForm,
  createInitialExpandedSteps,
  emptyAction,
  resolveInitialAuthoringMode,
  resolveInitialRuleName,
  scrollElementIntoContainer,
  type RuleAuthoringMode,
} from './rule-modal-helpers';
import { formatRulePreview, suggestRuleNameFromDescription } from './rule-preview';
import { isRuleValid } from './rule-validation';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import type {
  InterpretationRule,
  InterpretationRuleDraft,
  InterpretationRuleField,
  InterpretationRuleFieldOptions,
  ParseRuleIssue,
  RuleAction,
  RuleCondition,
  RuleConditionLogic,
  RuleConditionOperator,
} from './types';

type FlowStepState = 'pending' | 'active' | 'complete';
type DescribeStepKey = 'name' | 'describe';
type BuildStepKey = 'name' | 'when' | 'update' | 'review';

const DESCRIBE_STEP_KEYS: DescribeStepKey[] = ['name', 'describe'];
const BUILD_STEP_KEYS: BuildStepKey[] = ['name', 'when', 'update', 'review'];

const DESCRIBE_EXAMPLE_KEYS = [
  'organisation.extraction-rules.describe-example-1',
  'organisation.extraction-rules.describe-example-2',
  'organisation.extraction-rules.describe-example-3',
] as const;

function resolveFlowStepState(
  step: DescribeStepKey | BuildStepKey,
  mode: RuleAuthoringMode,
  params: {
    hasName: boolean;
    hasDescribeText: boolean;
    hasConditions: boolean;
    canSave: boolean;
  }
): FlowStepState {
  const { hasName, hasDescribeText, hasConditions, canSave } = params;

  if (step === 'name') {
    return hasName ? 'complete' : 'active';
  }
  if (mode === 'describe' && step === 'describe') {
    if (hasDescribeText) return 'complete';
    return 'active';
  }
  if (step === 'when') {
    return hasConditions ? 'complete' : 'pending';
  }
  if (step === 'update') {
    return canSave ? 'complete' : 'active';
  }
  return canSave ? 'active' : 'pending';
}

function resolveOverviewStepState(
  stepIndex: number,
  stepKey: DescribeStepKey | BuildStepKey,
  activeStepIndex: number,
  mode: RuleAuthoringMode,
  params: {
    hasName: boolean;
    hasDescribeText: boolean;
    hasConditions: boolean;
    canSave: boolean;
  }
): FlowStepState {
  const contentState = resolveFlowStepState(stepKey, mode, params);

  if (stepIndex === activeStepIndex) {
    return 'active';
  }

  return contentState === 'complete' ? 'complete' : 'pending';
}

function newRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function emptyCondition(): RuleCondition {
  return {
    id: newRowId(),
    field: null,
    operator: null,
    value: null,
    valueId: null,
  };
}

export interface InterpretationRuleModalProps {
  organisationId: string;
  fieldOptions: InterpretationRuleFieldOptions;
  initialRule?: Partial<InterpretationRule>;
  initialMode?: RuleAuthoringMode;
  expandAllSteps?: boolean;
  existingRules?: InterpretationRule[];
  onSave: (rule: InterpretationRule) => void | Promise<void>;
}

export const InterpretationRuleModal: React.FC<
  InterpretationRuleModalProps
> = ({
  organisationId,
  fieldOptions,
  initialRule,
  initialMode,
  expandAllSteps = false,
  existingRules,
  onSave,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const pwaMobileNav = usePwaMobileNavEnabled();

  const parsedRule = useSelector(getParsedRule);

  const isEditRule = Boolean(initialRule?.id);
  const [mode, setMode] = React.useState<RuleAuthoringMode>(() =>
    resolveInitialAuthoringMode(initialRule, initialMode)
  );
  const [describeText, setDescribeText] = React.useState(
    () => initialRule?.naturalLanguageSource ?? ''
  );
  const [parsing, setParsing] = React.useState(false);
  const [parseIssues, setParseIssues] = React.useState<ParseRuleIssue[]>([]);
  const [name, setName] = React.useState(() =>
    resolveInitialRuleName(initialRule, t)
  );
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [enabled, setEnabled] = React.useState(
    () => initialRule?.enabled ?? true
  );
  const [conditions, setConditions] = React.useState<RuleCondition[]>(() =>
    cloneConditionsForForm(initialRule?.conditions)
  );
  const [conditionLogic, setConditionLogic] =
    React.useState<RuleConditionLogic>(
      () => initialRule?.conditionLogic ?? 'AND'
    );
  const [actions, setActions] = React.useState<RuleAction[]>(() =>
    cloneActionsForForm(initialRule?.actions)
  );

  const [saving, setSaving] = React.useState(false);
  const [describeActiveStepIndex, setDescribeActiveStepIndex] =
    React.useState(0);
  const [buildActiveStepIndex, setBuildActiveStepIndex] = React.useState(0);
  const [describeExpandedSteps, setDescribeExpandedSteps] = React.useState<
    boolean[]
  >(() =>
    createInitialExpandedSteps('describe', {
      expandAllSteps,
      isEdit: isEditRule,
    })
  );
  const [buildExpandedSteps, setBuildExpandedSteps] = React.useState<boolean[]>(
    () =>
      createInitialExpandedSteps('build', {
        expandAllSteps,
        isEdit: isEditRule,
      })
  );
  const [nameTouched, setNameTouched] = React.useState(() =>
    Boolean(initialRule?.name?.trim())
  );

  const describeTextAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const saveInFlightRef = React.useRef(false);
  const describeParseFeedbackRef = React.useRef<HTMLDivElement | null>(null);
  const modalScrollBodyRef = React.useRef<HTMLDivElement | null>(null);

  const conditionsFieldSelect = React.useMemo(
    () => getInterpretationRuleFieldSelectOptions(t, fieldOptions),
    [fieldOptions, t]
  );

  const actionsFieldSelect = React.useMemo(
    () => getInterpretationRuleFieldSelectOptions(t, fieldOptions, true),
    [fieldOptions, t]
  );

  const getConditionOperatorOptions = React.useCallback(
    (field: InterpretationRuleField | null | undefined) =>
      getOperatorOptionsForField(field, t),
    [t]
  );

  const describeExamples = React.useMemo(
    () => DESCRIBE_EXAMPLE_KEYS.map((key) => t(key)),
    [t]
  );

  const isNewRule = !initialRule?.id;

  const canSave = isRuleValid({ name, conditions, actions });

  const validateUniqueName = React.useCallback(
    (candidate: string): string | null => {
      const trimmed = candidate.trim();
      if (!trimmed) return null;
      if (!existingRules?.length) return null;

      const normalized = trimmed.toLowerCase();
      const conflict = existingRules.some((rule) => {
        if (!rule?.name) return false;
        if (initialRule?.id && rule.id === initialRule.id) return false;
        return rule.name.trim().toLowerCase() === normalized;
      });

      if (!conflict) return null;
      return t('organisation.extraction-rules.name-duplicate-error');
    },
    [existingRules, initialRule?.id, t]
  );

  React.useEffect(() => {
    if (!expandAllSteps) return;
    setDescribeExpandedSteps([true, true]);
    setBuildExpandedSteps([true, true, true, true]);
  }, [expandAllSteps]);

  React.useEffect(() => {
    setParseIssues([]);
  }, [describeText]);

  React.useEffect(() => {
    if (parseIssues.length === 0) return;

    let cancelled = false;
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        scrollElementIntoContainer(
          describeParseFeedbackRef.current,
          modalScrollBodyRef.current,
          { align: 'end', padding: 20 }
        );
      });
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [parseIssues]);

  React.useEffect(() => {
    if (mode !== 'describe' || !describeExpandedSteps[1]) return;
    const frame = window.requestAnimationFrame(() => {
      describeTextAreaRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [mode, describeExpandedSteps]);

  const fieldOptionsMap = {
    contactName: fieldOptions.contactNameOptions,
    country: fieldOptions.countryOptions,
    accountCode: fieldOptions.accountOptions,
    taxName: fieldOptions.taxOptions,
    trackingOption1Id: fieldOptions.trackingOption1Options,
    trackingOption2Id: fieldOptions.trackingOption2Options,
    itemId: fieldOptions.itemOptions,
  };

  useEffect(() => {
    if (parsedRule) {
      setParsing(false);
      setConditions(matchParsedValues(parsedRule.conditions));
      setActions(matchParsedValues(parsedRule.actions));
      setConditionLogic(parsedRule.conditionLogic || 'OR');

      if (!nameTouched) {
        setName(
          suggestRuleNameFromDescription(
            describeText,
            {
              conditions: parsedRule.conditions ?? [],
              actions: parsedRule.actions ?? [],
              conditionLogic: parsedRule.conditionLogic,
            },
            t
          )
        );
      }

      setMode('build');
      setBuildActiveStepIndex(3);
      setBuildExpandedSteps([true, true, true, true]);
      window.requestAnimationFrame(() => {
        modalScrollBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });

      dispatch(parseRuleDone(null));
    }
  }, [describeText, dispatch, nameTouched, parsedRule, t]);

  const matchParsedValues = (
    list: RuleCondition[] | RuleAction[] | null | undefined
  ) => {
    if (!list) {
      return null;
    }

    for (const item of list) {
      const field = item.field || '';
      const options = fieldOptionsMap[field];

      if (!options) {
        continue;
      }

      if (
        'operator' in item &&
        operatorAllowsFreeTextValue(item.operator)
      ) {
        continue;
      }

      const matchingValue = findFuzzyMatch(item.value, options);
      if (matchingValue) {
        item.value = matchingValue?.label;
        item.valueId = matchingValue?.value;
      }
    }

    return list;
  };

  const renderAuthoringModeSwitch = () => (
    <AuthoringChoiceRow>
      <AuthoringChoiceCard
        type="button"
        $selected={mode === 'describe'}
        aria-pressed={mode === 'describe'}
        onClick={() => setMode('describe')}
      >
        <AuthoringChoiceIcon $selected={mode === 'describe'}>
          <EditOutlined />
        </AuthoringChoiceIcon>
        <AuthoringChoiceText>
          <AuthoringChoiceTitle>
            {t('organisation.extraction-rules.describe-path-title')}
          </AuthoringChoiceTitle>
          <AuthoringChoiceDescription>
            {t('organisation.extraction-rules.describe-path-description')}
          </AuthoringChoiceDescription>
        </AuthoringChoiceText>
        <AuthoringAiBadge aria-hidden title="AI">
          <IconFuzzyMapping />
        </AuthoringAiBadge>
      </AuthoringChoiceCard>
      <AuthoringChoiceOr aria-hidden>OR</AuthoringChoiceOr>
      <AuthoringChoiceCard
        type="button"
        $selected={mode === 'build'}
        aria-pressed={mode === 'build'}
        onClick={() => setMode('build')}
      >
        <AuthoringChoiceIcon $selected={mode === 'build'}>
          <BuildOutlined />
        </AuthoringChoiceIcon>
        <AuthoringChoiceText>
          <AuthoringChoiceTitle>
            {t('organisation.extraction-rules.build-path-title')}
          </AuthoringChoiceTitle>
          <AuthoringChoiceDescription>
            {t('organisation.extraction-rules.build-path-description')}
          </AuthoringChoiceDescription>
        </AuthoringChoiceText>
      </AuthoringChoiceCard>
    </AuthoringChoiceRow>
  );

  const expandedSteps =
    mode === 'describe' ? describeExpandedSteps : buildExpandedSteps;
  const activeStepIndex =
    mode === 'describe' ? describeActiveStepIndex : buildActiveStepIndex;
  const currentStepKeys =
    mode === 'describe' ? DESCRIBE_STEP_KEYS : BUILD_STEP_KEYS;

  const flowStepParams = {
    hasName: Boolean(name.trim()),
    hasDescribeText: Boolean(describeText.trim()),
    hasConditions: conditions.some(
      (condition) => condition.field != null && condition.operator != null
    ),
    canSave,
  };

  const getStepState = (stepIndex: number): FlowStepState =>
    resolveOverviewStepState(
      stepIndex,
      currentStepKeys[stepIndex],
      activeStepIndex,
      mode,
      flowStepParams
    );

  const isStepExpanded = (stepIndex: number) =>
    expandedSteps[stepIndex] ?? false;

  const toggleStep = (stepIndex: number) => {
    const setExpanded =
      mode === 'describe' ? setDescribeExpandedSteps : setBuildExpandedSteps;
    const setActive =
      mode === 'describe'
        ? setDescribeActiveStepIndex
        : setBuildActiveStepIndex;

    setExpanded((prev) => {
      const next = [...prev];
      next[stepIndex] = !next[stepIndex];
      return next;
    });
    setActive(stepIndex);
  };

  const renderRuleNameStepBody = () => (
    <RuleStepBody>
      <RuleNameInputWrap>
        <RuleNameInput
          $invalid={(nameTouched && !name.trim()) || Boolean(nameError)}
          width="100%"
          value={name}
          onChange={(e) => {
            const nextValue = e.target.value;
            setName(nextValue);
            if (nextValue.length > 0) {
              setNameTouched(true);
            }
            setNameError(
              nextValue.trim() ? validateUniqueName(nextValue) : null
            );
          }}
          onBlur={(e) => {
            const nextValue = e.currentTarget.value;
            if (!nextValue.trim()) return;
            setNameTouched(true);
            setNameError(validateUniqueName(nextValue));
          }}
          placeholder={t('organisation.extraction-rules.rule-name-placeholder')}
        />
        {nameError ? <RuleNameError>{nameError}</RuleNameError> : null}
      </RuleNameInputWrap>
    </RuleStepBody>
  );

  const renderDescribeStepBody = () => (
    <RuleStepBody>
      <DescribeInputBlock>
        <DescribeInputLabel htmlFor="extraction-rule-describe-input">
          {t('organisation.extraction-rules.describe-input-label')}
        </DescribeInputLabel>
        <DescribeFieldMentions
          id="extraction-rule-describe-input"
          textareaRef={describeTextAreaRef}
          value={describeText}
          onChange={setDescribeText}
          fieldOptions={fieldOptions}
          t={t}
          placeholder={t('organisation.extraction-rules.describe-placeholder')}
        />
        <DescribeInputHint>
          {t('organisation.extraction-rules.describe-examples')}
        </DescribeInputHint>
      </DescribeInputBlock>
      <DescribeExamplesSection>
        <DescribeExamplesHeading>
          {t('organisation.extraction-rules.describe-examples-heading')}
        </DescribeExamplesHeading>
        <DescribeExampleList>
          {describeExamples.map((example) => (
            <DescribeExampleChip
              key={example}
              type="button"
              onClick={() => {
                setDescribeText(example);
                describeTextAreaRef.current?.focus();
              }}
            >
              {example}
            </DescribeExampleChip>
          ))}
        </DescribeExampleList>
      </DescribeExamplesSection>
      {parseIssues.length > 0 ? (
        <DescribeParseFeedback ref={describeParseFeedbackRef}>
          {parseIssues.map((issue) => (
            <div key={issue.code}>
              <DescribeParseFeedbackHeader>
                <DescribeParseFeedbackIcon aria-hidden>
                  <WarningOutlined />
                </DescribeParseFeedbackIcon>
                <DescribeParseFeedbackText>
                  <DescribeParseFeedbackTitle>
                    {issue.title}
                  </DescribeParseFeedbackTitle>
                  <DescribeParseFeedbackDetail>
                    {issue.detail}
                  </DescribeParseFeedbackDetail>
                </DescribeParseFeedbackText>
              </DescribeParseFeedbackHeader>
              {issue.suggestions.length > 0 ? (
                <>
                  <DescribeParseSuggestionsHeading>
                    {t(
                      'organisation.extraction-rules.parse-suggestions-heading'
                    )}
                  </DescribeParseSuggestionsHeading>
                  <DescribeParseSuggestionList>
                    {issue.suggestions.map((suggestion) => (
                      <DescribeParseSuggestionChip
                        key={suggestion}
                        type="button"
                        onClick={() => {
                          setDescribeText(suggestion);
                          describeTextAreaRef.current?.focus();
                        }}
                      >
                        {suggestion}
                      </DescribeParseSuggestionChip>
                    ))}
                  </DescribeParseSuggestionList>
                </>
              ) : null}
            </div>
          ))}
        </DescribeParseFeedback>
      ) : null}
    </RuleStepBody>
  );

  const renderFieldSelect = (
    value: InterpretationRuleField | null | undefined,
    mode: 'conditions' | 'actions',
    onChange: (field: InterpretationRuleField) => void
  ) => (
    <RuleMapSelect
      {...MAP_SELECT_PROPS}
      value={value ?? undefined}
      placeholder={t('organisation.extraction-rules.select-value')}
      options={
        mode === 'conditions' ? conditionsFieldSelect : actionsFieldSelect
      }
      onChange={(v) => onChange(v as InterpretationRuleField)}
      optionRender={(option) => {
        const data = option.data as
          | InterpretationRuleFieldSelectOption
          | undefined;
        const label = data?.label ?? String(option.label ?? '');
        const description = data?.description ?? '';
        return (
          <FieldSelectOptionContent>
            <FieldOptionTitle>{label}</FieldOptionTitle>
            {description ? (
              <FieldOptionDescription>{description}</FieldOptionDescription>
            ) : null}
          </FieldSelectOptionContent>
        );
      }}
    />
  );

  const draftForPreview: Pick<
    InterpretationRule,
    'conditions' | 'actions' | 'conditionLogic'
  > = {
    conditions,
    actions,
    conditionLogic,
  };

  const renderStepCardHeader = (
    stepIndex: number,
    title: string,
    description?: string,
    badge?: 'optional' | 'required',
    action?: React.ReactNode
  ) => {
    const expanded = isStepExpanded(stepIndex);
    const stepState = getStepState(stepIndex);

    return (
      <RuleStepHeader
        $clickable
        $hasBody={expanded}
        aria-expanded={expanded}
        onClick={() => toggleStep(stepIndex)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleStep(stepIndex);
          }
        }}
      >
        <RuleStepHeaderText>
          <RuleStepTitleRow>
            <RuleStepNumber $state={stepState} aria-hidden>
              {stepState === 'complete' ? (
                <CheckOutlined style={{ fontSize: 11 }} />
              ) : (
                stepIndex + 1
              )}
            </RuleStepNumber>
            <RuleStepTitle>{title}</RuleStepTitle>
            {badge ? (
              <StepBadge $variant={badge}>
                {badge === 'required'
                  ? t('common.validation-required')
                  : t('organisation.extraction-rules.step-optional')}
              </StepBadge>
            ) : null}
          </RuleStepTitleRow>
          {expanded && description ? (
            <RuleStepDescription>{description}</RuleStepDescription>
          ) : null}
        </RuleStepHeaderText>
        {expanded && action ? (
          <RuleStepHeaderActions onClick={(e) => e.stopPropagation()}>
            {action}
          </RuleStepHeaderActions>
        ) : null}
        <RuleStepChevron $expanded={expanded} aria-hidden>
          <DownOutlined />
        </RuleStepChevron>
      </RuleStepHeader>
    );
  };

  const wrapStepCard = (
    stepIndex: number,
    header: React.ReactNode,
    body: React.ReactNode
  ) => {
    const expanded = isStepExpanded(stepIndex);

    return (
      <RuleStepCard $collapsed={!expanded} $active={expanded}>
        {header}
        {expanded ? body : null}
      </RuleStepCard>
    );
  };

  const renderValueControl = (
    field: InterpretationRuleField | null | undefined,
    value: string | number | null,
    onChange: (v: string | number | null, id?: string | null) => void,
    context?: { operator?: RuleConditionOperator | null }
  ) => {
    if (!field) {
      return (
        <RuleMapSelect
          {...MAP_SELECT_PROPS}
          disabled
          placeholder={t('organisation.extraction-rules.select-field-first')}
        />
      );
    }

    const options = getFieldValueOptions(field, fieldOptions);
    const allowFreeText =
      context?.operator != null &&
      operatorAllowsFreeTextValue(context.operator);

    if (options?.length && allowFreeText) {
      const displayValue = value != null ? String(value) : '';
      return (
        <RuleMapAutoComplete
          value={displayValue}
          options={options.map((option) => ({
            value: option.label,
            label: option.label,
          }))}
          placeholder={t(
            'organisation.extraction-rules.select-or-enter-value'
          )}
          filterOption={filterMapSelectOption}
          onSelect={(selected) => {
            const match = options.find((option) => option.label === selected);
            onChange(match?.label ?? selected, match?.value ?? null);
          }}
          onChange={(text) => {
            const next = String(text);
            const match = options.find(
              (option) =>
                option.label === next || String(option.value) === next
            );
            onChange(next, match?.value ?? null);
          }}
        />
      );
    }

    if (options?.length) {
      return (
        <RuleMapSelect
          {...MAP_SELECT_PROPS}
          value={resolveOptionValue(options, value)}
          options={options}
          onChange={(v) =>
            onChange(getFieldName(options, v), v as string)
          }
          placeholder={t('organisation.extraction-rules.select-value')}
        />
      );
    }

    return (
      <RuleMapInput
        width="60%"
        value={value != null ? String(value) : ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('organisation.enter-value')}
      />
    );
  };

  const resolveConditionValueForControl = (
    condition: RuleCondition
  ): string | number | null => {
    if (
      condition.operator &&
      operatorAllowsFreeTextValue(condition.operator)
    ) {
      if (condition.value != null && String(condition.value).trim() !== '') {
        return condition.value;
      }
      const options = getFieldValueOptions(condition.field, fieldOptions);
      if (condition.valueId && options?.length) {
        const match = options.find(
          (option) => String(option.value) === String(condition.valueId)
        );
        return match?.label ?? condition.valueId;
      }
      return condition.value;
    }

    return condition.valueId || condition.value;
  };

  const getFieldName = (elements, id) => {
    const foundElement = elements?.find(
      (el) => String(el.value) === String(id)
    );
    return foundElement ? foundElement.label : undefined;
  };

  const resolveOptionValue = (elements, value) => {
    const optionExists = elements?.some(
      (option) => String(option.value) === String(value)
    );

    return value != null && optionExists ? String(value) : undefined;
  };

  const handleBuildFromDescription = () => {
    if (!describeText.trim()) return;

    setParsing(true);
    // Clear previous parse response so we don't accidentally "complete" instantly.
    dispatch(parseRuleDone(null));
    dispatch(parseRuleRequest(describeText, getTcNames()));
  };

  React.useEffect(() => {
    if (!parsing) return;
    const timeout = window.setTimeout(() => {
      setParsing(false);
    }, 15000);
    return () => window.clearTimeout(timeout);
  }, [parsing]);

  /*const handleBuildFromDescription = async () => {
    if (!describeText.trim()) return;
    setParsing(true);
    setParseIssues([]);
    try {
      const result = await parseRuleFromText(describeText, t);
      const hasActions = (result.rule.actions?.length ?? 0) > 0;
      const hasIssues = result.issues.length > 0;

      setParseIssues(result.issues);
      setDescribeExpandedSteps([true, true]);
      setDescribeActiveStepIndex(1);

      if (!nameTouched) {
        setName(suggestRuleNameFromDescription(describeText, result.rule, t));
      }

      if (!hasActions || hasIssues) {
        return;
      }

      if (result.rule.conditions?.length) {
        setConditions(
          applyParsedFieldValuesToConditions(
            result.rule.conditions.map((c) => ({
              ...c,
              id: c.id || newRowId(),
            })),
            fieldOptions
          )
        );
        setConditionLogic(result.rule.conditionLogic ?? 'AND');
      } else {
        setConditions([]);
      }
      if (result.rule.actions?.length) {
        setActions(
          applyParsedFieldValuesToActions(
            result.rule.actions.map((a) => ({
              ...a,
              id: a.id || newRowId(),
            })),
            fieldOptions
          )
        );
      }
      setMode('build');
      setBuildActiveStepIndex(3);
      setBuildExpandedSteps([true, true, true, true]);
      window.requestAnimationFrame(() => {
        modalScrollBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } catch {
      setParseIssues([
        {
          code: 'parse_failed',
          title: t('organisation.extraction-rules.parse-error-failed-title'),
          detail: t('organisation.extraction-rules.parse-error-failed-detail'),
          suggestions: [],
        },
      ]);
      setDescribeExpandedSteps([true, true]);
      setDescribeActiveStepIndex(1);
      if (!nameTouched) {
        setName(
          suggestRuleNameFromDescription(
            describeText,
            { conditions: [], actions: [] },
            t
          )
        );
      }
    } finally {
      setParsing(false);
    }
  }; */

  const handleSave = async () => {
    if (!canSave || saving || saveInFlightRef.current) return;
    const uniqueError = validateUniqueName(name);
    if (uniqueError) {
      setNameTouched(true);
      setNameError(uniqueError);
      setBuildActiveStepIndex(0);
      setBuildExpandedSteps([true, true, true, true]);
      window.requestAnimationFrame(() => {
        modalScrollBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
      return;
    }

    saveInFlightRef.current = true;
    setSaving(true);
    try {
      const resolvedName = name.trim();
      if (!resolvedName) return;

      const rule: InterpretationRule = {
        id: initialRule?.id,
        applicationConnectionId: organisationId,
        name: resolvedName,
        enabled,
        conditionLogic: conditionLogic,
        conditions,
        actions,
        tcNames: getTcNames(),
      };
      await onSave(rule);
      dispatch(turnModalOff());
    } finally {
      saveInFlightRef.current = false;
      setSaving(false);
    }
  };

  const getTcNames = () => {
    return [
      fieldOptions?.trackingFieldLabels?.trackingOption1Id,
      fieldOptions?.trackingFieldLabels?.trackingOption2Id,
    ].filter((val) => val !== undefined && val !== null);
  };

  return (
    <>
      <ExtractionRulesSelectDropdownStyle />
      <ExtractionRulesFormControlStyle />
      <ExtractionRuleModalAntTypographyStyle />
      <ModalRoot
        className={`scope-extraction-rule-modal${
          pwaMobileNav ? ' scope-extraction-rule-modal--pwa' : ''
        }`}
      >
        <ModalContent>
          <ModalScrollBody
            ref={modalScrollBodyRef}
            tabIndex={0}
            role="region"
            aria-label={
              isNewRule
                ? t('organisation.extraction-rules.create-rule-title')
                : t('organisation.extraction-rules.edit-rule-title')
            }
          >
            {isNewRule ? (
              <Section>{renderAuthoringModeSwitch()}</Section>
            ) : null}

            {mode === 'describe' ? (
              <RuleBuilderFlow>
                {wrapStepCard(
                  0,
                  renderStepCardHeader(
                    0,
                    t('organisation.extraction-rules.step-name-title'),
                    undefined,
                    'optional'
                  ),
                  renderRuleNameStepBody()
                )}
                {wrapStepCard(
                  1,
                  renderStepCardHeader(
                    1,
                    t('organisation.extraction-rules.describe-panel-title'),
                    t(
                      'organisation.extraction-rules.step-describe-description'
                    ),
                    'required'
                  ),
                  renderDescribeStepBody()
                )}
              </RuleBuilderFlow>
            ) : (
              <RuleBuilderFlow>
                {wrapStepCard(
                  0,
                  renderStepCardHeader(
                    0,
                    t('organisation.extraction-rules.step-name-title'),
                    undefined,
                    'required'
                  ),
                  renderRuleNameStepBody()
                )}
                {wrapStepCard(
                  1,
                  renderStepCardHeader(
                    1,
                    t('organisation.extraction-rules.step-when-title'),
                    t('organisation.extraction-rules.step-when-description'),
                    'optional',
                    <StepActionButton
                      type="default"
                      width="fit-content"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        setConditions((prev) => [...prev, emptyCondition()])
                      }
                    >
                      {t('organisation.extraction-rules.add-condition')}
                    </StepActionButton>
                  ),
                  <RuleStepBody>
                    {conditions.length === 0 ? (
                      <StepEmptyHint>
                        {t('organisation.extraction-rules.when-empty-helper')}
                      </StepEmptyHint>
                    ) : (
                      <>
                        {conditions.length > 1 && (
                          <ConditionLogicRow>
                            <RuleFieldLabel style={{ flex: 'unset' }}>
                              {t(
                                'organisation.extraction-rules.condition-logic-label'
                              )}
                            </RuleFieldLabel>
                            <Segmented
                              size="small"
                              value={conditionLogic}
                              onChange={(value) =>
                                setConditionLogic(value as RuleConditionLogic)
                              }
                              options={[
                                {
                                  label: t(
                                    'organisation.extraction-rules.condition-logic-and'
                                  ),
                                  value: 'AND',
                                },
                                {
                                  label: t(
                                    'signup.OR'
                                  ),
                                  value: 'OR',
                                },
                              ]}
                            />
                          </ConditionLogicRow>
                        )}
                        {conditions.map((condition, index) => (
                          <React.Fragment key={condition.id}>
                            {index > 0 && (
                              <ConditionConnector>
                                {conditionLogic === 'OR'
                                  ? t(
                                      'signup.OR'
                                    )
                                  : t(
                                      'organisation.extraction-rules.condition-logic-and'
                                    )}
                              </ConditionConnector>
                            )}
                            <ConditionBlock>
                              <ConditionBlockHeader>
                                <span>
                                  {t(
                                    'organisation.extraction-rules.condition-label'
                                  )}{' '}
                                  {index + 1}
                                </span>
                                <DeleteOutlined
                                  style={{
                                    color: 'var(--color-text-muted, #5a6a6a)',
                                    cursor: 'pointer',
                                  }}
                                  onClick={() =>
                                    setConditions((prev) =>
                                      prev.filter((_, i) => i !== index)
                                    )
                                  }
                                />
                              </ConditionBlockHeader>
                              <RuleFieldList>
                                <RuleFieldItem>
                                  <RuleFieldLabel>
                                    {t('organisation.extraction-rules.field')}
                                  </RuleFieldLabel>
                                  {renderFieldSelect(
                                    condition.field,
                                    'conditions',
                                    (field) =>
                                      setConditions((prev) =>
                                        prev.map((c, i) =>
                                          i === index
                                            ? {
                                                ...c,
                                                field,
                                                operator: isOperatorValidForField(
                                                  field,
                                                  c.operator
                                                )
                                                  ? c.operator
                                                  : null,
                                                value: null,
                                                valueId: null,
                                              }
                                            : c
                                        )
                                      )
                                  )}
                                </RuleFieldItem>
                                <RuleFieldItem>
                                  <RuleFieldLabel>
                                    {t(
                                      'organisation.extraction-rules.operator'
                                    )}
                                  </RuleFieldLabel>
                                  <RuleMapSelect
                                    {...MAP_SELECT_PROPS}
                                    disabled={!condition.field}
                                    value={condition.operator ?? undefined}
                                    placeholder={t(
                                      'organisation.extraction-rules.select-value'
                                    )}
                                    options={getConditionOperatorOptions(
                                      condition.field
                                    )}
                                    onChange={(v) =>
                                      setConditions((prev) =>
                                        prev.map((c, i) =>
                                          i === index
                                            ? {
                                                ...c,
                                                operator:
                                                  v as RuleCondition['operator'],
                                                value: operatorNeedsValue(
                                                  v as RuleCondition['operator']
                                                )
                                                  ? c.value
                                                  : null,
                                                valueId: operatorNeedsValue(
                                                  v as RuleCondition['operator']
                                                )
                                                  ? c.valueId
                                                  : null,
                                              }
                                            : c
                                        )
                                      )
                                    }
                                  />
                                </RuleFieldItem>
                                {condition.operator &&
                                  operatorNeedsValue(condition.operator) && (
                                    <RuleFieldItem>
                                      <RuleFieldLabel>
                                        {t(
                                          'organisation.extraction-rules.value'
                                        )}
                                      </RuleFieldLabel>
                                      {renderValueControl(
                                        condition.field,
                                        resolveConditionValueForControl(
                                          condition
                                        ),
                                        (v, id) => {
                                          setConditions((prev) =>
                                            prev.map((c, i) =>
                                              i === index
                                                ? {
                                                    ...c,
                                                    value: v,
                                                    valueId: id ?? null,
                                                  }
                                                : c
                                            )
                                          );
                                        },
                                        { operator: condition.operator }
                                      )}
                                    </RuleFieldItem>
                                  )}
                              </RuleFieldList>
                            </ConditionBlock>
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </RuleStepBody>
                )}

                {wrapStepCard(
                  2,
                  renderStepCardHeader(
                    2,
                    t('organisation.extraction-rules.step-then-title'),
                    t('organisation.extraction-rules.step-then-description'),
                    'required',
                    <StepActionButton
                      type="default"
                      width="fit-content"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        setActions((prev) => [...prev, emptyAction()])
                      }
                    >
                      {t('organisation.extraction-rules.add-action')}
                    </StepActionButton>
                  ),
                  <RuleStepBody>
                    {actions.map((action, index) => (
                      <ConditionBlock key={action.id}>
                        <ConditionBlockHeader>
                          <span>
                            {t('common.update')}{' '}
                            {index + 1}
                          </span>
                          {actions.length > 1 && (
                            <DeleteOutlined
                              role="button"
                              tabIndex={0}
                              aria-label={t('common.delete')}
                              style={{ color: 'var(--color-text-muted, #5a6a6a)', cursor: 'pointer' }}
                              onClick={() =>
                                setActions((prev) =>
                                  prev.filter((_, i) => i !== index)
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setActions((prev) =>
                                    prev.filter((_, i) => i !== index)
                                  );
                                }
                              }}
                            />
                          )}
                        </ConditionBlockHeader>
                        <RuleFieldList>
                          <RuleFieldItem>
                            <RuleFieldLabel>
                              {t('organisation.extraction-rules.target-field')}
                            </RuleFieldLabel>
                            {renderFieldSelect(
                              action.field,
                              'actions',
                              (targetField) =>
                                setActions((prev) =>
                                  prev.map((a, i) =>
                                    i === index
                                      ? {
                                          ...a,
                                          field: targetField,
                                          value: null,
                                        }
                                      : a
                                  )
                                )
                            )}
                          </RuleFieldItem>
                          <RuleFieldItem>
                            <RuleFieldLabel>
                              {t('organisation.extraction-rules.target-value')}
                            </RuleFieldLabel>
                            {renderValueControl(
                              action.field,
                              action.valueId || action.value,
                              (v, id) => {
                                setActions((prev) =>
                                  prev.map((a, i) =>
                                    i === index
                                      ? { ...a, value: v, valueId: id }
                                      : a
                                  )
                                );
                              }
                            )}
                          </RuleFieldItem>
                        </RuleFieldList>
                      </ConditionBlock>
                    ))}
                  </RuleStepBody>
                )}

                {wrapStepCard(
                  3,
                  renderStepCardHeader(
                    3,
                    t('organisation.extraction-rules.step-review-title'),
                    t('organisation.extraction-rules.step-review-description')
                  ),
                  <RuleStepBody>
                    <PreviewBox>
                      {formatRulePreview(draftForPreview, t, fieldOptions) ||
                        t('organisation.extraction-rules.preview-empty')}
                    </PreviewBox>
                    <ReviewEnabledRow>
                      <ReviewEnabledLabel>
                        <ReviewEnabledTitle>
                          {t('common.enabled')}
                        </ReviewEnabledTitle>
                        <ReviewEnabledHint>
                          {t('organisation.extraction-rules.step-enabled-hint')}
                        </ReviewEnabledHint>
                      </ReviewEnabledLabel>
                      <Switch
                        checked={enabled}
                        onChange={setEnabled}
                        size="small"
                        aria-label={t('common.enabled')}
                      />
                    </ReviewEnabledRow>
                  </RuleStepBody>
                )}
              </RuleBuilderFlow>
            )}
          </ModalScrollBody>

          <ModalFooterBar>
            <FooterButtons>
              <FooterLeft>
                <FooterButton
                  type="default"
                  onClick={() => dispatch(turnModalOff())}
                >
                  {t('common.cancel')}
                </FooterButton>
              </FooterLeft>
              <FooterRight>
                <FooterRightButtons>
                  {mode === 'describe' && (
                    <FooterPrimaryButton
                      type="primary"
                      htmlType="button"
                      disabled={!describeText.trim() || parsing}
                      onClick={() => handleBuildFromDescription()}
                    >
                      <Space>
                        {parsing ? (
                          <>
                            <OrangeSpinnerRing aria-hidden>
                              <ActiveStepSpinner />
                            </OrangeSpinnerRing>
                            {t('organisation.extraction-rules.building')}
                          </>
                        ) : (
                          t(
                            'organisation.extraction-rules.build-from-description'
                          )
                        )}
                      </Space>
                    </FooterPrimaryButton>
                  )}
                  {mode === 'build' && (
                    <FooterPrimaryButton
                      type="primary"
                      disabled={!canSave || saving}
                      onClick={handleSave}
                    >
                      {t('organisation.extraction-rules.save-rule')}
                    </FooterPrimaryButton>
                  )}
                </FooterRightButtons>
              </FooterRight>
            </FooterButtons>
          </ModalFooterBar>
        </ModalContent>
      </ModalRoot>
    </>
  );
};

export function buildEmptyRuleDraft(
  partial: Partial<InterpretationRuleDraft> | undefined,
  t: TFunction
): Partial<InterpretationRule> {
  return {
    enabled: partial?.enabled ?? true,
    conditions: partial?.conditions ?? [],
    actions: partial?.actions?.length ? partial.actions : [emptyAction()],
    naturalLanguageSource: partial?.naturalLanguageSource,
    conditionLogic: partial?.conditionLogic,
    name: resolveInitialRuleName(partial, t),
  };
}
